import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { StatusPill } from '../../components/StatusPills';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Plus, Trash2, KeyRound, Power, PowerOff, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/ConfirmDialog';

const PROVIDER_TYPES = [
  { value: 'openrouter', label: 'OpenRouter', help: 'Unified gateway to Claude, GPT, Gemini, Llama and more.' },
  { value: 'anthropic', label: 'Anthropic (direct)', help: 'Direct Anthropic Messages API at api.anthropic.com.' },
];

const TYPE_LABEL = {
  openrouter: 'OpenRouter',
  anthropic: 'Anthropic',
};

const AdminProviders = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [testing, setTesting] = useState(null); // provider id currently testing
  const [testResults, setTestResults] = useState({}); // { providerId: { ok, error, ... } }

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.listProviders();
      setProviders(res.data?.providers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleTest = async (id) => {
    setTesting(id);
    setTestResults((prev) => ({ ...prev, [id]: null }));
    try {
      const res = await adminApi.testProvider(id);
      setTestResults((prev) => ({ ...prev, [id]: res.data }));
      if (res.data?.ok) {
        toast.success('Connection successful');
      } else {
        toast.error(`Connection failed: ${res.data?.error || 'unknown error'}`);
      }
    } catch (e) {
      const errStr = e?.response?.data?.detail || e?.message || 'Test failed';
      setTestResults((prev) => ({ ...prev, [id]: { ok: false, error: errStr } }));
      toast.error(`Test failed: ${errStr}`);
    } finally {
      setTesting(null);
    }
  };

  const handleToggle = async (p) => {
    try {
      await adminApi.updateProvider(p.id, { enabled: !p.enabled });
      await load();
      toast.success(`${p.name} ${!p.enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const confirmDialog = useConfirm();
  const handleDelete = async (p) => {
    if (!(await confirmDialog({ title: 'Delete this provider?', detail: p.name, description: 'This cannot be undone.', confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await adminApi.deleteProvider(p.id);
      await load();
      toast.success('Provider deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-providers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground">AI providers</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Configure which LLM providers the app can use. The system Anthropic provider is
            always available. Add OpenRouter or another Anthropic key to use specific models
            per process.
          </p>
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          data-testid="providers-add-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add provider
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          {providers.map((p) => {
            const tr = testResults[p.id];
            return (
              <Card
                key={p.id}
                data-testid={`provider-card-${p.id}`}
              >
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px] space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground text-base">{p.name}</span>
                        <Badge variant="secondary">{TYPE_LABEL[p.type] || p.type}</Badge>
                        {p.is_system && (
                          <Badge variant="secondary" className="text-[10px]">System</Badge>
                        )}
                        {p.enabled ? (
                          <StatusPill tone="done" size="sm">Enabled</StatusPill>
                        ) : (
                          <StatusPill tone="neutral" size="sm">Disabled</StatusPill>
                        )}
                      </div>
                      {p.api_key_masked ? (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <KeyRound className="w-3 h-3" /> API key: <span className="font-mono">{p.api_key_masked}</span>
                        </p>
                      ) : p.is_system ? (
                        <p className="text-xs text-muted-foreground">
                          Uses the backend ANTHROPIC_API_KEY. No admin-managed key.
                        </p>
                      ) : (
                        <p className="text-xs text-amber-ink">No API key set. Disabled until one is added.</p>
                      )}
                      {p.base_url && (
                        <p className="text-xs text-muted-foreground">Base URL: <span className="font-mono">{p.base_url}</span></p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={testing === p.id}
                        onClick={() => handleTest(p.id)}
                        data-testid={`provider-test-${p.id}`}
                      >
                        {testing === p.id ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        ) : null}
                        Test connection
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleToggle(p)}
                        data-testid={`provider-toggle-${p.id}`}
                      >
                        {p.enabled ? (
                          <><PowerOff className="w-3.5 h-3.5 mr-1.5" /> Disable</>
                        ) : (
                          <><Power className="w-3.5 h-3.5 mr-1.5" /> Enable</>
                        )}
                      </Button>
                      {!p.is_system && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(p)}
                          data-testid={`provider-delete-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {tr && (
                    <div
                      className={`rounded-lg p-3 text-xs ${
                        tr.ok ? 'bg-brand-wash text-brand-ink' : 'bg-breach-wash text-breach-ink'
                      }`}
                      data-testid={`provider-test-result-${p.id}`}
                    >
                      <div>
                        {tr.ok
                          ? `Connection healthy. ${tr.model_used ? `Routed to ${tr.model_used}. ` : ''}Sample: "${tr.sample_text || ''}"`
                          : `Failed: ${tr.error || 'unknown error'}`}
                      </div>
                    </div>
                  )}

                  {!p.is_system && (
                    <ProviderEditFields provider={p} onSaved={load} />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AddProviderDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        onCreated={load}
      />
    </div>
  );
};


// -----------------------------------------------------------------
// Inline edit fields for a non-system provider
// -----------------------------------------------------------------
const ProviderEditFields = ({ provider, onSaved }) => {
  const [name, setName] = useState(provider.name);
  const [baseUrl, setBaseUrl] = useState(provider.base_url || '');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);

  const dirty =
    name !== provider.name ||
    (baseUrl || '') !== (provider.base_url || '') ||
    apiKey.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const body = { name, base_url: baseUrl || null };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      await adminApi.updateProvider(provider.id, body);
      setApiKey('');
      toast.success('Provider updated');
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-rule pt-4 grid sm:grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Display name</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid={`provider-name-${provider.id}`}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Base URL (optional)</Label>
        <Input
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="Leave blank for default"
          data-testid={`provider-baseurl-${provider.id}`}
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label className="text-xs">Replace API key (write-only; leave blank to keep the existing key)</Label>
        <Input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={provider.api_key_masked || 'Paste new key…'}
          autoComplete="off"
          data-testid={`provider-apikey-${provider.id}`}
        />
      </div>
      <div className="sm:col-span-2 flex justify-end">
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`provider-save-${provider.id}`}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
};


// -----------------------------------------------------------------
// New-provider dialog
// -----------------------------------------------------------------
const AddProviderDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('openrouter');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName('');
    setType('openrouter');
    setApiKey('');
    setBaseUrl('');
    setEnabled(true);
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Name required');
      return;
    }
    if (!apiKey.trim()) {
      toast.error('API key required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createProvider({
        name: name.trim(),
        type,
        api_key: apiKey.trim(),
        base_url: baseUrl.trim() || null,
        enabled,
      });
      toast.success('Provider added');
      reset();
      onOpenChange(false);
      await onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add provider');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-provider-dialog">
        <DialogHeader>
          <DialogTitle>Add AI provider</DialogTitle>
          <DialogDescription>
            Configure an OpenRouter or Anthropic provider. The API key is encrypted at rest
            and never returned to the frontend after saving.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Display name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. OpenRouter (production)"
              data-testid="add-provider-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="add-provider-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDER_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {PROVIDER_TYPES.find((t) => t.value === type)?.help}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">API key</Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-..."
              autoComplete="off"
              data-testid="add-provider-apikey"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Base URL (optional)</Label>
            <Input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="Leave blank for default"
              data-testid="add-provider-baseurl"
            />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="add-provider-enabled"
            />
            <Label className="text-sm">Enabled</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="add-provider-submit"
          >
            {saving ? 'Adding…' : 'Add provider'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminProviders;
